import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useGetLocations } from '@repo/api/location/useGetLocations';
import { scheduleSlotToLocal, scheduleSlotToUtc } from '@repo/lib/time/scheduleSlotTimezone';
import { type GroupScheduleFormSchema, type GroupScheduleSchema, groupScheduleFormSchema } from '@repo/schemas/group/groupSchedule';
import { addHours, addMonths, format, subMonths, roundToNearestMinutes } from 'date-fns';
import { CalendarDaysIcon, CheckCircle2Icon, PlusIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Separator } from '@/components/ui/separator';
import { DEFAULT_TIME_STEP } from '@/utils/calendar';

import { GroupScheduleMeetingFormCard } from './GroupScheduleMeetingFormCard';

interface GroupScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: GroupScheduleSchema) => Promise<void> | void;
  initialValue?: GroupScheduleSchema;
  isPending?: boolean;
}

export function GroupScheduleDialog(groupScheduleDialogProps: GroupScheduleDialogProps) {
  const { open, onOpenChange, onSubmit, initialValue, isPending = false } = groupScheduleDialogProps;

  const { data: locations = [], isPending: isLocationsPending } = useGetLocations();

  const initialFormValue = useMemo(() => {
    if (!initialValue) return undefined;
    const skipHolidays = initialValue.meetings.length > 0 && initialValue.meetings.every((meeting) => meeting.skipHolidays);
    // The backend keeps slot times in UTC; the form edits them in local time.
    const meetings = initialValue.meetings.map((meeting) => scheduleSlotToLocal(meeting));
    return groupScheduleFormSchema.safeParse({ ...initialValue, meetings, skipHolidays }).data;
  }, [initialValue]);

  const form = useForm<GroupScheduleFormSchema>({
    mode: 'onBlur',
    resolver: standardSchemaResolver(groupScheduleFormSchema),
    defaultValues: {
      startDate: undefined,
      endDate: undefined,
      meetings: [],
      skipHolidays: false,
    },
    values: initialFormValue,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'meetings',
  });
  const startDateValue = useWatch({
    control: form.control,
    name: 'startDate',
  });
  const endDateValue = useWatch({
    control: form.control,
    name: 'endDate',
  });
  const meetingsValue =
    useWatch({
      control: form.control,
      name: 'meetings',
    }) ?? [];

  const skipHolidaysValue = useWatch({
    control: form.control,
    name: 'skipHolidays',
  });

  const skipHolidaysCheckboxState = useMemo(() => {
    if (meetingsValue.length === 0) return skipHolidaysValue ?? false;

    const checkedMeetingsCount = meetingsValue.filter((meeting) => meeting.skipHolidays).length;

    if (checkedMeetingsCount === 0) return false;
    if (checkedMeetingsCount === meetingsValue.length) return true;
    return 'indeterminate';
  }, [meetingsValue, skipHolidaysValue]);

  const handleSkipHolidaysChange = (checked: boolean | 'indeterminate') => {
    const skipHolidays = checked === true;

    form.setValue('skipHolidays', skipHolidays, {
      shouldDirty: true,
      shouldTouch: true,
    });
    form.getValues('meetings').forEach((_, index) => {
      form.setValue(`meetings.${index}.skipHolidays`, skipHolidays, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const appendNewMeeting = () => {
    append({
      startT: format(roundToNearestMinutes(new Date(), { roundingMethod: 'ceil', nearestTo: DEFAULT_TIME_STEP }), 'HH:mm'),
      endT: format(roundToNearestMinutes(addHours(new Date(), 1), { roundingMethod: 'ceil', nearestTo: DEFAULT_TIME_STEP }), 'HH:mm'),
      type: 'remote',
      name: 'Spotkanie',
      repeat: 'weekly',
      day: 1,
      skipHolidays: skipHolidaysCheckboxState === true,
      location: null,
      meetingURL: null,
    });
  };

  useEffect(() => {
    if (!open) return;
    form.reset(initialFormValue);
  }, [form, open, initialFormValue]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = async ({ skipHolidays: _skipHolidays, ...data }: GroupScheduleFormSchema) => {
    await onSubmit({ ...data, meetings: data.meetings.map((meeting) => scheduleSlotToUtc(meeting)) });
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'max-h-200 sm:h-[90dvh] sm:min-h-96 sm:max-w-130'} onCloseAutoFocus={handleClose} onEscapeKeyDown={handleClose}>
        <Form {...form}>
          <DialogBody>
            <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
              <FeaturedIcon size={'lg'}>
                <CalendarDaysIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Harmonogram</DialogTitle>
                <DialogDescription className={'text-muted-foreground text-xs'}>Zdefiniuj okres obowiązywania harmonogramu.</DialogDescription>
              </div>
            </DialogHeader>

            <form id="group-schedule-form" className={'flex grow flex-col gap-5 overflow-y-auto p-4'} onSubmit={form.handleSubmit(handleSubmit)} noValidate>
              <section className={'space-y-3'}>
                <div className={'space-y-1'}>
                  <h5 className={'text-lg font-medium'}>Przedział czasowy</h5>
                  <p className={'text-muted-foreground text-xs'}>Wybierz datę początkową i końcową okresu harmonogramu.</p>
                </div>

                <div className={'grid gap-2 sm:grid-cols-2'}>
                  <FormField
                    name={'startDate'}
                    control={form.control}
                    rules={{ deps: ['endDate'] }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data początkowa</FormLabel>
                        <FormControl>
                          <DatePicker
                            {...field}
                            selected={field.value ? new Date(field.value) : null}
                            onSelect={(date) => {
                              field.onChange(format(date, 'yyyy-MM-dd'));
                              field.onBlur();
                            }}
                            matcher={endDateValue ? { after: subMonths(new Date(endDateValue), 1) } : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name={'endDate'}
                    control={form.control}
                    rules={{ deps: ['startDate'] }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data końcowa</FormLabel>
                        <FormControl>
                          <DatePicker
                            {...field}
                            selected={field.value ? new Date(field.value) : null}
                            onSelect={(date) => {
                              field.onChange(format(date, 'yyyy-MM-dd'));
                              field.onBlur();
                            }}
                            matcher={startDateValue ? { before: addMonths(new Date(startDateValue), 1) } : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div
                  className={
                    'border-border-primary bg-card/35 has-data-[state=checked]:border-border-brand relative grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-2 gap-y-1 rounded-md border p-3'
                  }
                >
                  <Checkbox id="group-schedule-skip-holidays" className={'static mt-0.5 after:absolute after:inset-0'} checked={skipHolidaysCheckboxState} onCheckedChange={handleSkipHolidaysChange} />
                  <div className={'grid gap-1'}>
                    <Label htmlFor="group-schedule-skip-holidays">Pomijaj dni wolne</Label>
                    <p className={'text-muted-foreground text-xs'}>Nie twórz spotkań wypadających w okresie dni wolnych.</p>
                  </div>
                </div>
              </section>

              <Separator />

              <section className={'space-y-3'}>
                <div className={'space-y-1'}>
                  <h5 className={'text-lg font-medium'}>Harmonogram spotkania</h5>
                  <p className={'text-text-tertiary text-xs'}>Zdefiniuj szczegóły i formę każdego cyklicznego spotkania.</p>
                </div>

                <Accordion type={'single'} className={'gap-3'}>
                  {fields.map((field, index) => (
                    <GroupScheduleMeetingFormCard key={field.id} form={form} index={index} remove={remove} locations={{ data: locations, isPending: isLocationsPending }} />
                  ))}
                </Accordion>

                <Button type={'button'} variant={'outline'} className={'w-full border-dashed'} onClick={appendNewMeeting}>
                  <PlusIcon />
                  Dodaj
                </Button>
              </section>
            </form>
          </DialogBody>

          <DialogFooter className={'border-border-primary bg-accent dark:bg-card border-t'}>
            <Button onClick={handleClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>

            <Button disabled={isPending} form="group-schedule-form" type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <CheckCircle2Icon />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
